import {
  Divider,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

interface ActivityCardProps {
  title: string;
  /** Period menu(s) rendered on the right of the header. */
  actions?: ReactNode;
  children: ReactNode;
  /** Applied to the card shell — cards with a scrolling body cap their height. */
  sx?: SxProps<Theme>;
}

/**
 * White bordered shell shared by every card in the Activity section: title row,
 * divider, then the chart body. The body is the flex child that grows, so the
 * cards in a row always line up.
 */
export const ActivityCard = ({
  title,
  actions,
  children,
  sx,
}: ActivityCardProps) => {
  const theme = useTheme();

  return (
    <Stack
      sx={[
        {
          flex: 1,
          minWidth: 0,
          backgroundColor: theme.palette.neutral.main,
          border: `1px solid ${theme.palette.neutral["010"]}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          p: "12px 16px",
          minHeight: "44px",
        }}
      >
        <Typography
          variant="caption-2"
          sx={{ color: theme.palette.lightBg.high }}
          noWrap
        >
          {title}
        </Typography>
        {actions ? (
          <Stack direction="row" sx={{ alignItems: "center", gap: "4px" }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>

      <Divider sx={{ borderColor: theme.palette.neutral["010"] }} />

      <Stack sx={{ flex: 1, minHeight: 0, p: "16px", gap: "16px" }}>
        {children}
      </Stack>
    </Stack>
  );
};
